/* Header and search behaviour: navigation, the search panel and predictive results.
   Split out of theme.js so neither file approaches Shopify's 10KB per-file budget. */
(() => {
  'use strict';

  const on = (el, ev, fn) => el && el.addEventListener(ev, fn);
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Mobile navigation ---------- */
  on($('[data-toggle="mobile-nav"]'), 'click', (e) => {
    const button = e.currentTarget;
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    if (!panel) return;
    const open = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!open));
    panel.hidden = open;
  });

  /* ---------- Search panel ---------- */
  const searchToggle = $('[data-toggle="search"]');
  const searchPanel = document.getElementById('SearchPanel');

  const closeSearch = () => {
    if (!searchPanel) return;
    searchPanel.hidden = true;
    if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');
  };

  on(searchToggle, 'click', () => {
    if (!searchPanel) return;
    const open = searchToggle.getAttribute('aria-expanded') === 'true';
    searchPanel.hidden = open;
    searchToggle.setAttribute('aria-expanded', String(!open));
    if (!open) $('input[type="search"]', searchPanel)?.focus();
  });

  $$('[data-close-search]').forEach((btn) => on(btn, 'click', closeSearch));
  on(document, 'keydown', (e) => {
    if (e.key === 'Escape' && searchPanel && !searchPanel.hidden) {
      closeSearch();
      searchToggle?.focus();
    }
  });

  /* ---------- Predictive search ---------- */
  // One binding per form: the header panel and the collection sidebar each own a
  // results box, named by the input's aria-owns. Types and limit come from the form.
  $$('[data-predictive-search]').forEach((predictiveForm) => {
    const input = $('input[type="search"]', predictiveForm);
    const results = input && document.getElementById(input.getAttribute('aria-owns'));
    if (!input || !results) return;
    let timer;
    let controller;

    on(input, 'input', () => {
      clearTimeout(timer);
      const term = input.value.trim();

      if (term.length < 2) {
        results.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        return;
      }

      // Debounced so a fast typist does not fire a request per keystroke.
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q: term,
          section_id: 'predictive-search',
          'resources[type]': predictiveForm.dataset.predictiveTypes || 'product,collection,article,page',
          'resources[limit]': predictiveForm.dataset.predictiveLimit || '6',
        });
        try {
          const res = await fetch(`/search/suggest?${params}`, { signal: controller.signal });
          if (!res.ok) return;
          const html = await res.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          results.innerHTML = $('[data-predictive-results]', parsed)?.innerHTML ?? '';
          input.setAttribute('aria-expanded', 'true');
        } catch {
          // An aborted request is the normal case while typing, so stay quiet.
        }
      }, 250);
    });
  });

  /* ---------- Product recommendations ---------- */
  $$('[data-recommendations]').forEach(async (holder) => {
    const url = holder.dataset.url;
    if (!url) return;
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const parsed = new DOMParser().parseFromString(await res.text(), 'text/html');
      const fresh = $('[data-recommendations]', parsed);
      // No recommendations is a normal outcome, so remove the skeleton rather
      // than leaving an empty heading behind.
      if (!fresh || !$('.product-grid', fresh)) {
        holder.remove();
        return;
      }
      holder.innerHTML = fresh.innerHTML;
    } catch {
      holder.remove();
    }
  });

  /* ---------- Quick add ---------- */
  $$('[data-quick-add]').forEach((form) => {
    on(form, 'submit', async (e) => {
      if (!drawer) return;
      e.preventDefault();
      const button = $('button', form);
      const label = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = '...';
      }
      try {
        const res = await fetch('/cart/add.js', { method: 'POST', body: new FormData(form) });
        if (!res.ok) throw new Error('add failed');
        await refreshCart();
        drawer.open();
      } catch {
        form.submit();
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = label;
        }
      }
    });
  });

  /* ---------- Header dropdowns ---------- */
  const dropdowns = $$('.header__dropdown');

  if (dropdowns.length && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
    // Opening on hover is what shoppers expect on a desktop pointer. The details
    // element keeps working on click and with a keyboard, so nothing is lost when
    // hover is unavailable.
    let closeTimer;

    const closeAll = (except) => {
      dropdowns.forEach((d) => {
        if (d !== except) d.open = false;
      });
    };

    dropdowns.forEach((dropdown) => {
      const summary = $('summary', dropdown);

      on(dropdown, 'pointerenter', () => {
        clearTimeout(closeTimer);
        closeAll(dropdown);
        dropdown.open = true;
      });

      // With hover opening the panel, a click on the summary would toggle it shut
      // the instant it appeared. Follow the parent link instead, which is what a
      // shopper reaching for "Shop" actually wants.
      on(summary, 'click', (e) => {
        const href = summary.dataset.href;
        if (!href || href === '#') return;
        e.preventDefault();
        window.location.href = href;
      });
      on(dropdown, 'pointerleave', () => {
        // A short grace period, so crossing a gap does not slam the panel shut.
        closeTimer = setTimeout(() => (dropdown.open = false), 180);
      });
      on(dropdown, 'focusout', (e) => {
        if (!dropdown.contains(e.relatedTarget)) dropdown.open = false;
      });
    });

    on(document, 'keydown', (e) => {
      if (e.key === 'Escape') closeAll(null);
    });
  }
})();
